import { Link } from "react-router";
import {
  Building2,
  ExternalLink,
  Linkedin,
  MapPin,
  MessageSquare,
  Sparkles,
} from "lucide-react";

import {
  Avatar as ShadcnAvatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import type { Contact } from "../types";
import type { SwipeScenario } from "./types";
import type { SwipeSuggestion } from "./useSwipeSuggestions";

/** Days after which a relationship reads as gone quiet. Matches RelationshipBrief. */
const STALE_DAYS = 60;

const daysSince = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
};

const humanizeAge = (days: number | null): string => {
  if (days === null) return "no recorded touch";
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  if (days < 365) {
    const months = Math.round(days / 30);
    return `${months} ${months === 1 ? "month" : "months"} ago`;
  }
  const years = Math.round(days / 365);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
};

/**
 * The one line under the name.
 *
 * `li_current_title` is the real job title; `contacts.title` was historically
 * imported from the LinkedIn headline and is marketing copy on roughly a
 * quarter of rows, so it is only a fallback here.
 */
const roleLine = (contact: Contact): string | null => {
  const title = contact.li_current_title || contact.title;
  const company = contact.li_current_company || contact.company_name;
  if (title && company) return `${title} · ${company}`;
  return title || company || null;
};

const placeLine = (contact: Contact): string | null =>
  [contact.city, contact.region, contact.country].filter(Boolean).join(", ") ||
  null;

/**
 * A fixed instant for probing which fields an action writes. The keys of a
 * patch never depend on the clock; only the values do.
 */
const SAMPLE_NOW = new Date(0);

/** What the other scenarios have already settled, shown so you can see it. */
const CONTEXT_CHIPS: { field: keyof Contact & string; label: string }[] = [
  { field: "status", label: "Level" },
  { field: "buyer_proximity", label: "Proximity" },
  { field: "fit_quality", label: "Fit" },
  { field: "relationship_types", label: "Roles" },
  { field: "relationship_motion", label: "Motion" },
  { field: "connection_pipeline_stage", label: "Stage" },
];

export const SwipeCard = ({
  contact,
  scenario,
  suggestion,
  className,
  isBackdrop = false,
}: {
  contact: Contact;
  scenario: SwipeScenario;
  /** The first-pass recommendation, when this contact was scored. */
  suggestion?: SwipeSuggestion;
  className?: string;
  /** The card peeking out behind the active one — inert and dimmed. */
  isBackdrop?: boolean;
}) => {
  // Chips for the columns this scenario's answers would change are filled in,
  // so you can see what you are about to overwrite. Actions declare their
  // fields by writing them, so this asks each one for a sample patch rather
  // than making scenarios repeat the list.
  const written = new Set(
    scenario.actions.flatMap((action) =>
      Object.keys(action.patch(contact, SAMPLE_NOW)),
    ),
  );
  const age = daysSince(contact.last_meaningful_interaction_at);
  const role = roleLine(contact);
  const place = placeLine(contact);
  const name =
    [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
    "Unnamed contact";

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-2xl border bg-card shadow-lg",
        isBackdrop && "pointer-events-none",
        className,
      )}
    >
      <div className="flex shrink-0 items-start gap-3 border-b p-4">
        <ShadcnAvatar className="h-14 w-14 shrink-0">
          <AvatarImage src={contact.avatar?.src ?? undefined} alt="" />
          <AvatarFallback className="text-base">
            {contact.first_name?.charAt(0).toUpperCase()}
            {contact.last_name?.charAt(0).toUpperCase()}
          </AvatarFallback>
        </ShadcnAvatar>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg leading-tight font-semibold">
            {name}
          </h2>
          {role && (
            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
              {role}
            </p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {place && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" aria-hidden />
                {place}
              </span>
            )}
            <span
              className={cn(
                "flex items-center gap-1",
                age !== null && age > STALE_DAYS && "text-destructive",
              )}
            >
              <MessageSquare className="h-3 w-3" aria-hidden />
              {humanizeAge(age)}
            </span>
          </div>
        </div>
      </div>

      {/*
        The card is a fixed height so the answer buttons never move between
        cards, which means long evidence gets clipped. The mask fades the last
        few pixels so a cut line reads as "scroll for more" rather than as a
        broken layout — which is exactly how a hard clip read in testing.
      */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 [mask-image:linear-gradient(to_bottom,black_calc(100%-1.25rem),transparent)]">
        {suggestion && (
          // The recommendation goes above everything else: the card exists so
          // the question is "is this right?", and that needs the claim first
          // and the evidence for it underneath.
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-primary uppercase">
              <Sparkles className="h-3 w-3" aria-hidden />
              Suggests {suggestion.suggested_value ?? "no change"}
              <span className="ml-auto tabular-nums opacity-70">
                {suggestion.score}
              </span>
            </p>
            <p className="mt-1 text-sm leading-snug">{suggestion.rationale}</p>
            {suggestion.evidence && (
              <p className="mt-1 text-[11px] leading-snug text-muted-foreground italic">
                {suggestion.evidence}
              </p>
            )}
          </div>
        )}
        {contact.li_headline && (
          <p className="text-sm leading-snug text-muted-foreground italic">
            “{contact.li_headline}”
          </p>
        )}

        {contact.relationship_evidence && (
          <Field label="Evidence" value={contact.relationship_evidence} />
        )}
        {contact.next_relationship_move && (
          <Field label="Next move" value={contact.next_relationship_move} />
        )}
        {contact.background && !contact.relationship_evidence && (
          <Field label="Background" value={contact.background} />
        )}

        <div className="flex flex-wrap gap-1.5 pt-1">
          {CONTEXT_CHIPS.map(({ field, label }) => {
            const value = contact[field];
            if (value == null || value === "") return null;
            return (
              <Badge
                key={field}
                variant={written.has(field) ? "default" : "secondary"}
                className="text-[11px] font-normal"
              >
                <span className="opacity-60">{label}:</span>&nbsp;
                {String(value)}
              </Badge>
            );
          })}
        </div>
      </div>

      {!isBackdrop && (
        <div className="flex shrink-0 items-center gap-4 border-t px-4 py-2 text-xs">
          {contact.linkedin_url && (
            <a
              href={contact.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <Linkedin className="h-3.5 w-3.5" aria-hidden />
              LinkedIn
            </a>
          )}
          {contact.company_id != null && (
            <Link
              to={`/companies/${contact.company_id}/show`}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <Building2 className="h-3.5 w-3.5" aria-hidden />
              {contact.company_name ?? "Company"}
            </Link>
          )}
          <Link
            to={`/contacts/${contact.id}/show`}
            className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            Full record
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
      {label}
    </p>
    <p className="line-clamp-4 text-sm">{value}</p>
  </div>
);
